import { useState, useEffect } from 'react';
import { X, Phone, MessageSquare, Clock, Pencil, Loader2 } from 'lucide-react';
import { updateLead } from '../../../services/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'Новый' },
  { value: 'processing', label: 'В работе' },
  { value: 'won', label: 'Успешно' },
  { value: 'rejected', label: 'Отказ' },
];

const EVENT_LABELS = {
  'new': { label: 'Новый лид', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  'processing': { label: 'Взято в работу', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  'rejected': { label: 'Отказ / Архив', color: 'text-rose-400', bg: 'bg-rose-500/10' },
  'won': { label: 'Успешная сделка', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  'note': { label: 'Итог касания', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  'system': { label: 'Система', color: 'text-slate-500', bg: 'bg-slate-500/10' }
};

const formatDescription = (desc) => {
  if (!desc) return '';
  return desc
    .replace('Статус изменен на:', 'Статус изменен на')
    .replace('rejected', '«Отказ»')
    .replace('won', '«Успешно»')
    .replace('processing', '«В работе»')
    .replace('new', '«Новый»')
    .replace('Действие выполнено:', '');
};
export const LeadDetailsModal = ({ isOpen, onClose, lead, onRefresh }) => {
  const [localLead, setLocalLead] = useState(lead || null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    client_name: '',
    phone: '',
    description: '',
    price: '',
    status: 'new',
  });

  useEffect(() => {
    if (lead) {
      setLocalLead(lead);
      setForm({
        title: lead.title || '',
        client_name: lead.client_name || '',
        phone: lead.phone || '',
        description: lead.description || '',
        price: lead.price ?? '',
        status: lead.status || 'new',
      });
      setIsEditing(false);
    }
  }, [lead]);

  if (!isOpen || !lead) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateLead(lead.id, {
        title: form.title,
        client_name: form.client_name,
        phone: form.phone || null,
        description: form.description || null,
        price: form.price === '' ? 0 : Number(form.price),
        status: form.status,
      });
      setLocalLead(updated);
      setIsEditing(false);
      onRefresh?.();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Не удалось сохранить';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const displayLead = localLead || lead;

  return (
    <div className="fixed inset-0 z-[110] flex justify-end md:justify-center md:items-center p-0 md:p-4 bg-slate-950/60 backdrop-blur-md transition-all duration-300">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full max-w-lg md:max-w-2xl bg-[#1e293b] h-full md:h-auto md:max-h-[90vh] flex flex-col border-l border-white/5 md:border md:border-white/10 md:rounded-2xl md:shadow-2xl overflow-hidden">
        {/* Шапка: в адаптиве как панель, на десктопе — заголовок модалки */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-white/5 bg-[#1e293b] flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1 pr-2">
              <span className="text-[8px] sm:text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] sm:tracking-[0.3em]">Карточка сделки</span>
              <h2 className="text-base sm:text-xl md:text-3xl font-black text-white tracking-tight mt-0.5 sm:mt-1 truncate">{displayLead.title}</h2>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  type="button"
                  className="p-2 sm:p-3 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px] flex items-center justify-center bg-white/5 hover:bg-indigo-500/20 text-slate-400 hover:text-indigo-400 rounded-lg sm:rounded-2xl transition-all border border-white/5"
                  title="Редактировать"
                >
                  <Pencil size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
              ) : null}
              <button
                onClick={onClose}
                type="button"
                className="p-2 sm:p-3 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px] flex items-center justify-center bg-white/5 hover:bg-rose-500/20 text-slate-400 hover:text-rose-500 rounded-lg sm:rounded-2xl transition-all border border-white/5"
                aria-label="Закрыть"
              >
                <X size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>
          </div>
        </div>
       <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 min-h-0 bg-[#1e293b] custom-scrollbar">
          {isEditing ? (
            <div className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 sm:mb-1.5">Название сделки</label>
                <input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-slate-500 outline-none focus:border-indigo-500/50"
                  placeholder="Например: Ремонт квартиры"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 sm:mb-1.5">Имя клиента</label>
                  <input
                    value={form.client_name}
                    onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-slate-500 outline-none focus:border-indigo-500/50"
                    placeholder="ФИО или компания"
                  />
                </div>
                <div>
                  <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 sm:mb-1.5">Телефон</label>
                  <input
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-slate-500 outline-none focus:border-indigo-500/50"
                    placeholder="+7 ..."
                  />
                </div>
              </div>
              <div>
                <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 sm:mb-1.5">Описание</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-slate-500 outline-none focus:border-indigo-500/50 resize-y"
                  placeholder="Заметки после созвона..."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 sm:mb-1.5">Бюджет (₸)</label>
                  <input
                    type="number"
                    min={0}
                    value={form.price}
                    onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-slate-500 outline-none focus:border-indigo-500/50"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 sm:mb-1.5">Статус</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white outline-none focus:border-indigo-500/50"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-slate-900">{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3 pt-1 sm:pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 sm:py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white text-xs sm:text-sm font-black uppercase rounded-lg sm:rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 size={16} className="animate-spin sm:w-[18px] sm:h-[18px]" /> : null}
                  Сохранить
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 text-slate-300 text-xs sm:text-sm font-bold rounded-lg sm:rounded-xl transition-all"
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-slate-900/50 p-4 sm:p-6 rounded-xl sm:rounded-[2rem] border border-white/5">
                <p className="text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-0.5 sm:mb-1">Клиент</p>
                <p className="text-sm sm:text-base font-bold text-white">{displayLead.client_name || '—'}</p>
              </div>
              
              <div className="relative group">
                <a 
                  href={`tel:${displayLead.phone}`} 
                  className="bg-emerald-500/5 p-4 sm:p-6 rounded-xl sm:rounded-[2rem] border border-emerald-500/10 flex items-center justify-between hover:border-emerald-500/40 transition-all h-full"
                >
                  <div>
                    <p className="text-[9px] sm:text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-0.5 sm:mb-1">Телефон</p>
                    <p className="text-sm sm:text-base font-bold text-white">{displayLead.phone || '—'}</p>
                  </div>
                  <Phone size={18} className="sm:w-5 sm:h-5 text-emerald-500 opacity-40 group-hover:opacity-100 transition-all" />
                </a>
                
               {displayLead.phone && (
                <a
                  href={`https://wa.me/${displayLead.phone.replace(/\D/g, '')}?text=${encodeURIComponent(
                    `Здравствуйте, ${displayLead.client_name || ''}! Меня зовут ${displayLead.manager?.name || '...'}, я представляю компанию «Үркер». Пишу вам по поводу вашей заявки "${displayLead.title}". Подскажите, пожалуйста, вам удобно сейчас обсудить детали?`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute -right-1.5 -top-1.5 sm:-right-2 sm:-top-2 bg-emerald-600 hover:bg-emerald-500 text-white p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-xl transition-transform hover:scale-110 active:scale-95 flex items-center justify-center z-10 border-2 sm:border-4 border-[#1e293b]"
                  title="Написать в WhatsApp"
                >
                  <MessageSquare size={14} className="sm:w-4 sm:h-4" fill="currentColor" />
                </a>
              )}
              </div>
            </div>

            <div className="bg-indigo-500/5 p-4 sm:p-6 rounded-xl sm:rounded-[2rem] border border-indigo-500/10 mt-0">
              <p className="text-[9px] sm:text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
                <div className="h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-indigo-500 flex-shrink-0" /> 
                Описание сделки
              </p>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed italic">
                {displayLead.description || 'Описание отсутствует...'}
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <h3 className="text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] sm:tracking-[0.2em] flex items-center gap-1.5 sm:gap-2">
                <Clock size={12} className="sm:w-[14px] sm:h-[14px] text-indigo-500" /> События
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {displayLead.activities?.map((item) => {
                  const config = EVENT_LABELS[item.type] || EVENT_LABELS.system;
                  
                  return (
                    <div key={item.id} className="group relative pl-6 sm:pl-8 border-l border-white/5 ml-3 sm:ml-4 pb-2 last:pb-0">
                      <div className="absolute -left-[7px] sm:-left-[9px] top-1 h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[#1e293b] border-2 border-slate-700 flex items-center justify-center group-hover:border-indigo-500 transition-colors z-10">
                        <div className={`h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full ${config.color.replace('text', 'bg')}`} />
                      </div>

                      <div className={`p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-white/[0.03] transition-all hover:bg-white/[0.02] ${config.bg}`}>
                        <div className="flex justify-between items-center mb-0 gap-2">
                          <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-wider ${config.color}`}>
                            {config.label}
                          </span>
                          <span className="text-[8px] sm:text-[9px] font-bold text-slate-500 bg-black/20 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md sm:rounded-lg flex-shrink-0">
                            {new Date(item.created_at).toLocaleString('ru-RU')}
                          </span>
                        </div>
                        
                        <p className="text-xs sm:text-sm text-slate-200 font-medium leading-relaxed break-words whitespace-pre-wrap mt-0.5">
                          {formatDescription(item.description)}
                        </p>

                        {item.next_date && (
                          <div className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] font-bold text-indigo-400 bg-indigo-500/10 w-fit px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-indigo-500/20">
                            <Clock size={9} className="sm:w-2.5 sm:h-2.5" />
                            Следующий контакт: {new Date(item.next_date).toLocaleDateString('ru-RU')}
                          </div>
                        )}
                      </div>
                  </div>
    );
  })}
              </div>
            </div>
          </div>
          )}
      </div>
      </div>
    </div>
  );
};
